import { DB_TYPE } from '@/common/define/common.define';
import {
  getGetV7OracleActiveSessionDetailSqlTextQueryKey,
  getV7OracleActiveSessionDetailSqlText,
} from '@/api-client/oracle/queries/oracle-v7-controller/oracle-v7-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from '@/database/adapter/openDetail.define';
import type {
  DocumentTextParams,
  ParameterDetailParams,
  SessionDetailParams,
  SqlDetailParams,
} from '../types';
import { formatName, mergeDetailInfo, parseSchema } from './utils';
import {
  sessionDetailSchema,
  sqlDetailSchema,
  parameterDetailSchema,
  documentTextSchema,
  type SessionDetailSchema,
  type OracleSessionDetailAddition,
  type SqlDetailSchema,
  type OracleSqlDetailAddition,
  type ParameterDetailSchema,
  type OracleParameterDetailAddition,
  type DocumentTextSchema,
} from './oracle.schema';
import { formatTimeRange } from '@/database/utils/format.utils';

export const getOracleSessionDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  isOpenedFromPA,
  init,
  instanceId,
}: SessionDetailParams): { name: string; addition: OracleSessionDetailAddition } => {
  const { data } = parseSchema<SessionDetailSchema>(
    sessionDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime, isOpenedFromPA, init }),
    DATABASE_DETAIL_TYPE.SESSION,
  );

  return {
    name: formatName(data.sid),
    addition: {
      dbType: DB_TYPE.ORACLE,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      sid: data.sid,
      serialNumber: data.serialNumber,
      isOpenedFromPA: data.isOpenedFromPA,
      init: data.init,
    },
  };
};

export const getOracleSqlDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: SqlDetailParams): { name: string; addition: OracleSqlDetailAddition } => {
  const { data } = parseSchema<SqlDetailSchema>(
    sqlDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.SQL,
  );

  return {
    name: formatName(data.sqlId),
    addition: {
      dbType: DB_TYPE.ORACLE,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      sqlId: data.sqlId,
      module: data.module,
      planHashValue: data.planHashValue,
      childNumber: data.childNumber,
      programId: data.programId,
      programLine: data.programLine,
    },
  };
};

export const getOracleParameterDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: ParameterDetailParams): { name: string; addition: OracleParameterDetailAddition } => {
  const { data } = parseSchema<ParameterDetailSchema>(
    parameterDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.PARAMETER,
  );

  return {
    name: formatName(data.parameterName),
    addition: {
      dbType: DB_TYPE.ORACLE,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getOracleDocumentTextParams = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
  period,
  useFieldValidation,
}: DocumentTextParams) => {
  const formattedTime = formatTimeRange({ fromTime, toTime });
  const timeInfo = period ? { period } : formattedTime;

  if (!useFieldValidation) {
    return {
      instanceId,
      ...timeInfo,
      sqlId: detailInfo.sqlId,
    };
  }

  const { data } = parseSchema<DocumentTextSchema>(
    documentTextSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DOCUMENT_TEXT_TYPE,
  );

  return {
    instanceId: data.instanceId,
    ...timeInfo,
    sqlId: data.sqlId,
  };
};

export const fetchOracleDocumentText = async ({
  frameName,
  abortController,
  ...rest
}: DocumentTextParams) => {
  const params = getOracleDocumentTextParams({ ...rest, useFieldValidation: true });

  const {
    data: { data = [] },
  } = await getV7OracleActiveSessionDetailSqlText(
    params,
    { params: { frameName: frameName || FRAME_NAMES.DOCUMENT_WINDOW.FULL_TEXT } },
    abortController?.signal,
  );

  return data[0]?.sqlText || null;
};

export const getOracleDocumentTextQueryKey = (params: DocumentTextParams | null) => {
  if (!params) {
    return getGetV7OracleActiveSessionDetailSqlTextQueryKey();
  }

  const convertedParams = getOracleDocumentTextParams({ ...params, useFieldValidation: false });

  return getGetV7OracleActiveSessionDetailSqlTextQueryKey(convertedParams);
};

export const oracleSqlDetailRequiredMap = getRequiredMap(sqlDetailSchema);
export const oracleSessionDetailRequiredMap = getRequiredMap(sessionDetailSchema);
export const oracleDocumentTextRequiredMap = getRequiredMap(documentTextSchema);
